// context/NotificationContext.tsx
"use client";

import { postWithAuth } from "@/lib/api-helpers";
import { Notification } from "@/types/notification";
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { usePathname } from "next/navigation";
import { eventBus } from "@/services-frontend/comment/eventBus";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>; 
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaceContext();
  const pathname = usePathname();
  const isFormPage = pathname?.startsWith('/form/');  

  useEffect(() => {
  }, [notifications]);

  const fetchNotifications = async () => {
    try{
        const response = await postWithAuth('/api/notification/get-all',{
        }) 
        const notifications = Array.isArray(response.notifications) ? response.notifications : []
        
        setNotifications(notifications);
    }
    catch(error){
    }
  };


  // Unread count — per-user `read` flag comes from /get-all.
  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    // Skip API calls on form pages
    if (isFormPage) return;
    if (user?.email && currentWorkspace?._id) {
        fetchNotifications();   // ✅ refetch when user enters workspace
    }
  }, [user?.email , currentWorkspace?._id, isFormPage]);

  // Refetch on socket-triggered invalidation and on reconnect.
  // socketEventRouter calls fetchNotifications directly for legacy reasons,
  // but we also subscribe so we recover from missed events on reconnect.
  useEffect(() => {
    if (isFormPage) return;
    if (!user?.email) return;
    const handler = () => { fetchNotifications(); };
    eventBus.on("notifications:invalidate", handler);
    eventBus.on("socket:reconnected", handler);
    return () => {
      eventBus.off("notifications:invalidate", handler);
      eventBus.off("socket:reconnected", handler);
    };
  }, [user?.email, isFormPage]);

  return (
    <NotificationContext.Provider value={{ notifications,setNotifications, unreadCount, fetchNotifications}}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
};
